import { useState, useEffect, useCallback } from "react";
import { caseListContainerClasses } from "../../../components/common/caseListClasses";
import 'react-datepicker/dist/react-datepicker.css';
import { useParams } from "react-router-dom";
import HorizontalGraph from "./components/HorizontalGraph";
import useAxios from "../../../hooks/useAxios";
import Constants from "../../../Constants";
import { toaster } from "../../../utils/toast";
import XLoader from "../../../components/common/XLoader";
import { formatMillisecondsTohours, toTitleCase } from "../../../Utils";
import { formatUTCDate, daysAgo } from "@demandproai/shared";
import DateRangePicker from "./components/DateRangePicker";
import HelpIcon from "../../../../src/assets/icons/HelpIcon.svg?react";
import BarChart from "./components/BarChart";
import StatsCard from "./components/StatsCard";
import { pageTitleClasses } from '../../../components/common/PageTitle';

// The dashboard sits on its own tinted panel, inset from the page, with the chart
// card floating on it as a lighter surface.
const dashboardPanelClasses = "bg-surface rounded-md p-16";

const headerClasses = "flex justify-between items-center mb-6";

// The header row owns the spacing below the title, so the shared heading style
// drops its own bottom margin here.
const dashboardHeadingClasses = `${pageTitleClasses} mb-0!`;

const subscriptionTextClasses = "mb-0 text-sm text-content";
const graphHeaderClasses = "flex items-center";
const graphLabelClasses = "mr-4 text-2xl font-semibold text-content";
const graphsWrapClasses = "mb-6";

const graphRowClasses =
    "row py-6 px-5 flex-row ml-0 rounded-lg bg-surface shadow-md";

const graphTabsClasses = "flex gap-0 p-1 rounded bg-surface-alt";

const graphTabBaseClasses =
    "text-xl px-2.5 py-0.5 rounded min-w-25 border cursor-pointer transition-colors";
const graphTabActiveClasses =
    "border-border-light bg-surface text-content font-semibold";
const graphTabInactiveClasses =
    "border-transparent bg-transparent text-subtle";

const graphContainerClasses =
    "relative w-full h-[88%] flex flex-col items-center";

const paginationRowClasses =
    "flex justify-between items-center w-full min-h-[calc(var(--spacing)*10.5)]";

const graphActionsClasses =
    "flex items-center " +
    "[&_a]:text-base [&_a]:text-primary! [&_a]:font-semibold " +
    "[&_a:last-child]:pl-2.5";

const graphLinkClasses = "leading-3.5";

const placeholderTextClasses = "mb-0 text-sm text-content";

const statsCardsRowClasses = "row mb-0!";

// A flat Stone block with Ocean type, replacing the pre-rebrand blue radial gradient.
const averageTimePanelClasses =
    "col-md-5 flex flex-col justify-center rounded-lg py-25 px-6 items-start bg-surface-alt";

const averageTimeLabelClasses = "text-sm font-bold mb-0 text-primary";

// The duration reads as the panel's headline, so it carries the display size while
// the trailing unit stays at body scale.
const averageTimeValueClasses =
    "mb-5 text-3xl text-primary [&>span]:text-page-title [&>span]:leading-none";

const averageTimeCountClasses = "bg-transparent p-0 text-2xl text-primary capitalize";

const Dashboard = () => {
    const { firm } = useParams();

    const [userAnalyticDataPoint, setUserAnalyticDataPoint] = useState([]);
    const [monthlyAnalyticDataPoint, setMonthlyAnalyticDataPoint] = useState([]);
    const [averageTimeTaken, setAverageTimeTaken] = useState("");
    const [totalNumCases, setTotalNumCases] = useState("");
    const [cardDetailData, setCardDetailData] = useState(null);
    const [isActiveCredit, setIsActiveCredit] = useState(false);
    const [isActiveAccrued, setIsActiveAccrued] = useState(false);
    const [isActiveCarte, setIsActiveCarte] = useState(false);
    const [selectedRange, setSelectedRange] = useState({ startDate: daysAgo(30), endDate: new Date() });
    const [companyDetail, setCompanyDetail] = useState("");
    const [companyName, setCompanyName] = useState("");

    const { postData: userAnalyticsPostData, isLoading: userAnalyticsIsLoading } = useAxios()
    const { postData: monthlyPostData } = useAxios()

    const getUserAnalytics = useCallback(async () => {
        const { success, data, message } = await userAnalyticsPostData(Constants.ApiUrl.company.dashboard.userAnalytics, { selectedRange: selectedRange, databaseName: firm ? firm : null });
        if (success) {
            const { topUserAnalytics, caseOverview, companyDetail, companyName } = data;
            setUserAnalyticDataPoint(topUserAnalytics);
            setAverageTimeTaken(caseOverview?.averageTimeTaken);
            setCompanyDetail(companyDetail);
            setCompanyName(companyName);
            setTotalNumCases(caseOverview?.totalCases);
        } else {
            toaster({ message, success });
        }
    }, [selectedRange, userAnalyticsPostData, firm]);

    const getMonthlyAnalytics = useCallback(async () => {
        const { success, data, message } = await monthlyPostData(Constants.ApiUrl.company.dashboard.monthlyAnalytics, {databaseName: firm ? firm : null});

        if (success) {
            const { last12MonthData, cardDetail } = data;
            console.log(cardDetail)
            setMonthlyAnalyticDataPoint(last12MonthData);
            setCardDetailData(cardDetail)
        } else {
            toaster({ message, success });
        }
    }, [monthlyPostData, firm]);

    useEffect(() => {
        getUserAnalytics()
    }, [selectedRange, getUserAnalytics])

    useEffect(() => {
        getMonthlyAnalytics()
    }, [getMonthlyAnalytics])

    const [isOpenUserGraph, setOpenGraphData] = useState(true);

    const handleViewAll = () => {
        setSelectedRange({});
    }

    return (
        <section>
            <div className={`container ${caseListContainerClasses}`}>
                <div className={dashboardPanelClasses}>
                    <div className={graphsWrapClasses}>
                        <div className={headerClasses}>
                            <h1 className={dashboardHeadingClasses}>{toTitleCase(companyName)} Dashboard</h1>
                            <p className={subscriptionTextClasses}>Your subscription renews {formatUTCDate(companyDetail?.subscriptionRenewalDate)}</p>
                        </div>
                        <div className={graphRowClasses}>
                            <div className="col-md-7">
                                <div className={graphHeaderClasses}>
                                    <div className={graphLabelClasses}>Demands generated by</div>
                                    <div className={graphTabsClasses} role="group" aria-label="Graph data toggle">
                                        <button
                                            type="button"
                                            onClick={() => setOpenGraphData(true)}
                                            aria-pressed={isOpenUserGraph}
                                            className={`${graphTabBaseClasses} ${isOpenUserGraph ? graphTabActiveClasses : graphTabInactiveClasses}`}
                                        >
                                            User
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setOpenGraphData(false)}
                                            aria-pressed={!isOpenUserGraph}
                                            className={`${graphTabBaseClasses} ${!isOpenUserGraph ? graphTabActiveClasses : graphTabInactiveClasses}`}
                                        >
                                            Month
                                        </button>
                                    </div>
                                </div>
                                <div className={graphContainerClasses}>
                                    {(userAnalyticsIsLoading && !isOpenUserGraph) && <XLoader />}
                                    {isOpenUserGraph ? <HorizontalGraph dataPoints={userAnalyticDataPoint} totalNumCases={totalNumCases} graph={true} /> : <BarChart dataPoints={monthlyAnalyticDataPoint} />}
                                    <div className={paginationRowClasses}>
                                        <p className={placeholderTextClasses}></p>
                                        <div className={graphActionsClasses}>
                                            <DateRangePicker
                                                defaultRange={selectedRange}
                                                onRangeChange={(ranges) => {
                                                    setSelectedRange({ ...ranges })
                                                }}
                                            />
                                            <a onClick={() => handleViewAll()} className={graphLinkClasses}>View All</a>
                                        </div>
                                    </div>
                                </div>

                            </div>
                            <div className={averageTimePanelClasses}>
                                <div className={averageTimeLabelClasses}>Average Demand Creation Time:</div>
                                <div className={averageTimeValueClasses}><span>{formatMillisecondsTohours(averageTimeTaken)}</span></div>
                                <div className={averageTimeCountClasses}>{totalNumCases} demands</div>
                            </div>
                        </div>
                    </div>
                    <div className={statsCardsRowClasses}>
                        <div className="col-md-3">
                            <StatsCard
                                icon={<HelpIcon />}
                                title="Credit Balance"
                                count={<><span>{cardDetailData?.monthlyRemainingDemand}</span> / {cardDetailData?.demandsPerMonth}</>}
                                description={`Your current plan is ${cardDetailData?.demandsPerMonth} credits per month. You have ${cardDetailData?.monthlyRemainingDemand} credits remaining.`}
                                linkText="Upgrade your plan"
                                flippable
                                isFlipped={isActiveCredit}
                                onToggle={() => setIsActiveCredit(!isActiveCredit)}
                            />
                        </div>
                        <div className="col-md-3">
                            <StatsCard
                                icon={<HelpIcon />}
                                title="Accrued"
                                count={<><span>{cardDetailData?.rollOverCredits}</span></>}
                                description="Accrued credits will be used first before applying next month's credits."
                                linkText="Manage your plan"
                                flippable
                                isFlipped={isActiveAccrued}
                                onToggle={() => setIsActiveAccrued(!isActiveAccrued)}
                            />
                        </div>
                        <div className="col-md-3">
                            <StatsCard
                                icon={<HelpIcon />}
                                title="A La Carte"
                                count={<><span>{cardDetailData?.monthlyAlaCarteDemand}</span> this month</>}
                                description="Credits that will be billed for at the end of this month."
                                linkText="Manage your plan"
                                flippable
                                isFlipped={isActiveCarte}
                                onToggle={() => setIsActiveCarte(!isActiveCarte)}
                            />
                        </div>
                        <div className="col-md-3">
                            <StatsCard
                                title="Users"
                                count={<><span>{cardDetailData?.totalUsers}</span></>}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Dashboard;
